import React, { useCallback, useMemo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { curry, map, getOr, get, flow, keyBy, mapValues, keys } from 'lodash/fp';

import { userName } from '../../../../base/cookies-factory';
import { useModalProviderContext } from '../ModalProvider';
import { mandiEditAttributesModal, mandiEditAttributesModalTitle } from '../ModalProvider/modalNames';
import DimensionEditor from './DimensionEditor';
import CloseIcon from '../../themes/svg/closeIcon';
import Checkbox from '../FormElements/Checkbox';
import { measurementCategories, mountTypes } from './ListItems';
import { getAttribute } from '../../helpers/getAttributeValue';
import { editableAttributes } from '../../actions/editMandiAttributes/getChangedAttributes';
import {
  ResponsiveMandiEditAttributesModal,
  ModalTitle,
  ModalBody,
  Dimensions,
  GccDropdown,
  GccInput,
  GccTextArea,
  Verification,
  Error,
  SaveButton,
  CloseModalButton,
} from './styles';

const useEditableAttributes = (item) => {
  const itemId = get('itemId', item);

  return useMemo(() => (
    map(
      getAttribute(curry.placeholder, item),
      keys(editableAttributes),
    )
  ), [itemId]);
};

const getDefaultValues = initialValues => ({
  userName: userName(),
  verified: false,
  reason: '',
  ...flow(
    keyBy('name'),
    mapValues('normalizedValue'),
  )(initialValues),
});

const schema = yup.object().shape({
  category: yup.string().required(),
  reason: yup.string().required('Reason is required.'),
  verified: yup.bool().oneOf([true], 'Please verify that the new category can be used.'),
});

const MandiEditAttributesModal = ({
  editMandiAttributes,
  setLoading,
  globalError,
  items,
}) => {
  const {
    isOpen,
    setIsOpen,
    extras: { itemId },
  } = useModalProviderContext(mandiEditAttributesModal);

  const item = useMemo(() => get(itemId, items), [itemId]);
  const attributes = useEditableAttributes(item);

  const modalTitle = useMemo(
    () => `${mandiEditAttributesModalTitle} - ${get('label', item)}`,
    [item],
  );

  const onSubmit = useCallback(({ reason, ...newValues }) => {
    (async () => {
      try {
        setLoading(true);
        await editMandiAttributes(
          attributes,
          newValues,
          reason,
          get('designId', item),
          itemId,
        );

        setLoading(false);
        setIsOpen(false);
      } catch (err) {
        globalError({ err });
      }
    })();
  }, [itemId, attributes, setLoading, globalError]);

  const {
    register, errors, handleSubmit, formState, control, reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: getDefaultValues(attributes),
  });
  useEffect(() => reset(getDefaultValues(attributes)), [attributes]);

  return (
    <ResponsiveMandiEditAttributesModal
      id="mandiEditAttributesModal"
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      style={{
        overlay: {
          zIndex: 100,
          backgroundColor: 'rgba(0, 0, 0, .8)',
        },
      }}
    >
      <ModalTitle>
        {modalTitle}
        <CloseModalButton id="closeModalButton" title="close" onClick={() => setIsOpen(false)} tabIndex="-1" role="button">
          <CloseIcon />
        </CloseModalButton>
      </ModalTitle>
      <ModalBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <GccInput>
            <input id="userName" name="userName" disabled ref={register} />
            <label htmlFor="userName">User</label>
          </GccInput>
          <GccDropdown>
            <select id="category" name="category" ref={register}>
              <option />
              {map(({ value, display }) => (
                <option key={value} value={value}>{display}</option>
              ))(measurementCategories)}
            </select>
            <label htmlFor="category">
                New Category
            </label>
          </GccDropdown>
          <GccDropdown>
            <select id="mountType" name="mountType" readOnly ref={register}>
              <option />
              {map(({ value, display }) => (
                <option key={value} value={value}>{display}</option>
                ))(mountTypes)}
            </select>
            <label htmlFor="mountType">
                Mount
            </label>
          </GccDropdown>
          <Dimensions>
            <Controller
              as={DimensionEditor}
              control={control}
              name="width"
              label="width"
              minInches={7}
              maxInches={196}
            />
            <Controller
              as={DimensionEditor}
              control={control}
              name="height"
              label="height"
              minInches={10}
              maxInches={196}
            />
            <Controller
              as={DimensionEditor}
              control={control}
              name="depth"
              label="depth"
              minInches={0}
              maxInches={24}
            />
          </Dimensions>
          <GccTextArea>
            <textarea id="reason" name="reason" ref={register({ required: true })} />
            <label htmlFor="reason">Please Enter Reason</label>
          </GccTextArea>
          <Error>{getOr('', 'reason.message', errors)}</Error>
          <Verification>
            <Controller
              control={control}
              name="verified"
              render={({ onChange, value, ...props }) => (
                <Checkbox
                  {...props}
                  label="I have verified that the new category can be used with the current measurements (Height, Width and Depth)"
                  checked={value}
                  onChange={e => onChange(e.target.checked)}
                />
              )}
            />
          </Verification>
          <Error>{getOr('', 'verified.message', errors)}</Error>
          <SaveButton disabled={!formState.isValid}>Save</SaveButton>
        </form>
      </ModalBody>
    </ResponsiveMandiEditAttributesModal>);
};
MandiEditAttributesModal.propTypes = {
  editMandiAttributes: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  globalError: PropTypes.func.isRequired,
  items: PropTypes.shape({}).isRequired,
};

export default MandiEditAttributesModal;
